export const GITLAB_TOKEN_SCOPES = ['read_user', 'api'] as const;
export const GITLAB_USER_PROXY_PATH = '/gitlab/user-api';
export const GITLAB_MR_LIMIT = 8;

export type GitlabMergeRequestSource = 'authored' | 'review';

export type GitlabMergeRequest = {
  id: number;
  iid: number;
  title: string;
  webUrl: string;
  repository: string;
  source: GitlabMergeRequestSource;
};

export type GitlabMergeRequestApiItem = {
  id: number;
  iid: number;
  title: string;
  web_url: string;
  references?: {
    full?: string;
  };
};

export class GitlabAuthRequiredError extends Error {
  readonly name = 'GitlabAuthRequiredError';

  constructor() {
    super('GitLab authorization is required');
  }
}

export function isGitlabAuthRequiredError(error: unknown): boolean {
  return error instanceof GitlabAuthRequiredError;
}

export function isGitlabUnauthorizedStatus(status: number): boolean {
  return status === 401;
}

export function gitlabRepositoryName(
  webUrl: string,
  referencesFull?: string,
): string {
  const fromReferences = referencesFull?.replace(/!\d+$/, '').trim();
  const projectPath = fromReferences || projectPathFromWebUrl(webUrl);
  const parts = projectPath.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? projectPath;
}

function projectPathFromWebUrl(webUrl: string): string {
  try {
    const { pathname } = new URL(webUrl);
    const marker = '/-/merge_requests';
    const idx = pathname.indexOf(marker);
    return (idx >= 0 ? pathname.slice(0, idx) : pathname).replace(/^\//, '');
  } catch {
    return '';
  }
}

export function isGitlabMergeRequestApiItem(
  value: unknown,
): value is GitlabMergeRequestApiItem {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const item = value as Record<string, unknown>;
  return (
    typeof item.id === 'number' &&
    typeof item.iid === 'number' &&
    typeof item.title === 'string' &&
    typeof item.web_url === 'string'
  );
}

export function mergeGitlabMergeRequests(
  authored: GitlabMergeRequestApiItem[],
  review: GitlabMergeRequestApiItem[],
  limit: number = GITLAB_MR_LIMIT,
): GitlabMergeRequest[] {
  const seen = new Set<number>();
  const result: GitlabMergeRequest[] = [];

  const push = (
    item: GitlabMergeRequestApiItem,
    source: GitlabMergeRequestSource,
  ) => {
    if (seen.has(item.id) || result.length >= limit) {
      return;
    }
    seen.add(item.id);
    result.push({
      id: item.id,
      iid: item.iid,
      title: item.title,
      webUrl: item.web_url,
      repository: gitlabRepositoryName(item.web_url, item.references?.full),
      source,
    });
  };

  for (const item of authored) {
    push(item, 'authored');
  }
  for (const item of review) {
    push(item, 'review');
  }
  return result;
}
